<script setup lang="ts">
import { TIME_SLOT_GRID } from '../constants'
import type { SlotVisualState } from '../types'

const props = defineProps<{
  slotStates: Record<string, SlotVisualState>
  embedded: boolean
  isRescheduleMode: boolean
}>()

const emit = defineEmits<{
  'pick-slot': [slot: string]
  'occupied-enter': [payload: { slot: string; event: MouseEvent }]
  'occupied-leave': []
}>()

function onOccupiedEnter(slot: string, event: MouseEvent) {
  if (!props.slotStates[slot]?.showHover) return
  emit('occupied-enter', { slot, event })
}

function onPickSlot(slot: string) {
  emit('pick-slot', slot)
}
</script>

<template>
  <div
    class="slot-grid"
    :class="{
      'schedule-ctx--embedded': embedded,
      'schedule-ctx--reschedule': isRescheduleMode,
    }"
  >
    <template v-for="(row, rowIndex) in TIME_SLOT_GRID" :key="`slot-row-${rowIndex}`">
      <template v-for="(slot, colIndex) in row" :key="`slot-${rowIndex}-${colIndex}`">
        <button
          v-if="slot"
          type="button"
          class="slot-btn"
          :class="{
            'slot-btn--disabled': slotStates[slot]?.disabled,
            'slot-btn--booking-end': slotStates[slot]?.bookingEnd,
            'slot-btn--own-order': slotStates[slot]?.ownOrder,
            'slot-btn--selected': slotStates[slot]?.selected,
          }"
          :disabled="slotStates[slot]?.buttonDisabled"
          :aria-disabled="slotStates[slot]?.ariaDisabled || undefined"
          @mouseenter="onOccupiedEnter(slot, $event)"
          @mouseleave="emit('occupied-leave')"
          @click="onPickSlot(slot)"
        >
          {{ slot }}
        </button>
        <span v-else class="slot-grid-cell slot-grid-cell--empty" aria-hidden="true" />
      </template>
    </template>
  </div>
</template>

<style scoped>
.slot-grid.schedule-ctx--embedded {
  position: absolute;
  left: 17px;
  right: auto;
  bottom: 246px;
  flex: none;
  width: 554px;
  min-width: 554px;
  max-width: 554px;
  height: 204px;
  min-height: 204px;
  max-height: 204px;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(5, 98px);
  grid-template-rows: repeat(5, 28px);
  column-gap: 16px;
  row-gap: 16px;
  align-content: start;
  justify-content: start;
  box-sizing: border-box;
}

.slot-grid.schedule-ctx--embedded .slot-grid-cell--empty {
  width: 98px;
  height: 28px;
  min-height: 28px;
}

.slot-grid {
  flex: 1;
  min-height: 0;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  grid-template-rows: repeat(5, 28px);
  column-gap: 15px;
  row-gap: 20px;
  align-content: start;
  padding-bottom: 16px;
  box-sizing: border-box;
}

.slot-grid-cell--empty {
  display: block;
  min-height: 28px;
}

.slot-btn {
  width: 98px;
  min-width: 98px;
  max-width: 98px;
  height: 28px;
  min-height: 28px;
  max-height: 28px;
  padding: 4px 19px;
  box-sizing: border-box;
  border: 1px solid #e0e0e0;
  border-radius: 24px;
  background: #ffffff;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-weight: 400;
  font-size: 14px;
  font-style: normal;
  line-height: 20px;
  letter-spacing: 0;
  color: #000000;
  text-align: left;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  justify-self: center;
  white-space: nowrap;
}

.slot-btn:hover:not(:disabled):not(.slot-btn--selected) {
  border-color: #bdbdbd;
}

.slot-btn.slot-btn--selected {
  width: 98px;
  min-width: 98px;
  max-width: 98px;
  height: 28px;
  min-height: 28px;
  max-height: 28px;
  border: none;
  border-radius: 24px;
  background: #00aeef;
  color: #ffffff;
}

.slot-btn.slot-btn--selected:hover {
  width: 98px;
  height: 28px;
  border: none;
  border-radius: 24px;
  background: #00aeef;
  color: #ffffff;
}

.slot-btn.slot-btn--disabled,
.slot-btn:disabled {
  width: 98px;
  min-width: 98px;
  max-width: 98px;
  height: 28px;
  min-height: 28px;
  max-height: 28px;
  background: #cccccc;
  border: none;
  border-radius: 24px;
  padding: 4px 19px;
  box-sizing: border-box;
  font-family: SFPro, SFPro, 'SF Pro', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif;
  font-weight: 400;
  font-size: 14px;
  font-style: normal;
  line-height: 20px;
  letter-spacing: 0;
  color: #ffffff;
  text-align: left;
  cursor: default;
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  justify-self: center;
  white-space: nowrap;
}

.slot-btn.slot-btn--disabled:hover,
.slot-btn:disabled:hover {
  width: 98px;
  height: 28px;
  background: #cccccc;
  border: none;
  border-radius: 24px;
}

.slot-btn.slot-btn--booking-end {
  width: 98px;
  min-width: 98px;
  max-width: 98px;
  height: 28px;
  background: #cccccc;
  border: none;
  border-radius: 24px;
  color: #ffffff;
  cursor: default;
}

.slot-btn.slot-btn--booking-end:hover {
  background: #cccccc;
}

.slot-btn.slot-btn--own-order,
.slot-btn.slot-btn--own-order:disabled {
  background: #d9f0ff;
  border: none;
  border-radius: 24px;
  color: #000000;
  cursor: default;
}

.slot-btn.slot-btn--own-order:hover,
.slot-btn.slot-btn--own-order:disabled:hover {
  background: #d9f0ff;
  border: none;
  color: #000000;
}

.schedule-ctx--reschedule .slot-btn.slot-btn--selected,
.schedule-ctx--reschedule .slot-btn.slot-btn--selected:hover,
.schedule-ctx--reschedule .slot-btn.slot-btn--selected:disabled,
.schedule-ctx--reschedule .slot-btn.slot-btn--selected:disabled:hover {
  background: #00aeef;
  border: none;
  color: #ffffff;
}
</style>
